import { FC, useContext, useEffect, useState } from "react";

import { SxProps, styled, Button, Stack, Alert, Typography, Chip, IconButton } from "@mui/material";
import Drawer from "@mui/material/Drawer";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import BasketItem from "../BasketItem/basket-item";

import { BasketContext } from "../../../Layouts/Dashboard/dashboard";
import { Link as RouterLink, useLocation } from "react-router";
import { routePaths } from "../../../Router/router";
import { useDispatch, useSelector } from "react-redux";
import { GetBasketItems, GetBasketPanelMessage, SetLastPath } from "../../../Redux/Reducers/redux-slice-basket";
import { GroupByToMap } from "../../../Utils/helpers";
import { BasketQuickviewItem } from "../../../Core/Api/Types/basket-quickview-item";
import { ObjectLayer } from "../../../Core/Api/Types/object-layer";
import { OrderButton } from "../../Topbars/DashboardTopbar/PageHeadActions/page-head-order";
import { Close, ShoppingBagOutlined } from "@mui/icons-material";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import { themes } from "@mobiscroll/react";
import { useTranslation } from "react-i18next";

export interface DrawerProps {
  sx?: SxProps;
}

export const BASKETPANEL_WIDTH = 320;

export interface BasketPanelItems {
  id: number;
  name: string;
  parentLayer: ObjectLayer;
  items: BasketPanelGroupedItems[];
}
export interface BasketPanelGroupedItems {
  layer: ObjectLayer;
  items: BasketQuickviewItem[];
}

const PanelOrder: FC<DrawerProps> = (props) => {
  const { t } = useTranslation();
  const location = useLocation();
  const dispatch = useDispatch();
  const { openBasket, setOpenBasket } = useContext(BasketContext);

  // Redux
  const basketItems = useSelector(GetBasketItems);
  const basketMessage = useSelector(GetBasketPanelMessage);

  const [groupedItems, setGroupedItems] = useState<BasketPanelItems[]>([]);

  useEffect(() => {
    // Group items by title
    if (basketItems === null || basketItems === undefined) return;

    let grouped = GroupByToMap(basketItems, (x) => x.titleId);
    let output: BasketPanelItems[] = [];

    grouped.forEach((value: BasketQuickviewItem[], key: number | null) => {
      let titleGroup: BasketPanelItems = {
        id: key!,
        name: value[0].parentName!,
        parentLayer: value[0].parentLayerClass!,
        items: [],
      };

      let groupByLayer = GroupByToMap(value, (x) => x.layerClass);

      groupByLayer.forEach((items: BasketQuickviewItem[], key: ObjectLayer | null) => {
        let itemsByLayer: BasketPanelGroupedItems = {
          layer: key!,
          items: items,
        };

        titleGroup.items.push(itemsByLayer);
      });

      output.push(titleGroup);
    });

    setGroupedItems(output);
  }, [basketItems]);

  const handleStorePath = () => {
    dispatch(SetLastPath(location.pathname));
  };

  return (
    <OrderDrawer variant="permanent" anchor="right" open={openBasket}>
      <Stack p={1} pl={0} height="100%">
        <Stack
          sx={(theme) => ({
            borderRadius: 1,
            backgroundColor: theme.palette.background.paper,
            ...theme.helpers(theme).panelHighlights,
            height: "100%",
            overflowY: "hidden",
          })}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            pr={1}
            sx={(theme) => ({ backgroundColor: theme.palette.primary.alpha })}
          >
            <OrderButton
              onClick={() => setOpenBasket(false)}
              openBasket={openBasket}
              sx={{ height: GlobalConst.HEADER_HEIGHT, justifyContent: "left", width: "100%" }}
            >
              <ShoppingBagOutlined sx={{ mr: 1, color: "text.primary" }} />
              <Typography color="text.primary" id="tutorial-uikit-demo-orderPanel" component="span" fontWeight={500}>
                {t("component:body.myBasket")}
              </Typography>
              <Chip color="primary" size="small" label={basketItems?.length} sx={{ ml: 1 }} />
            </OrderButton>
            <IconButton color="ghost" size="small" onClick={() => setOpenBasket(false)}>
              <Close />
            </IconButton>
          </Stack>
          <Stack
            sx={(theme) => ({
              flexShrink: 1,
              overflowY: "scroll",
              minHeight: 0,
              ...theme.helpers(theme).scrollBar,
            })}
            py={2}
            pl={2}
            pr={1}
            spacing={2}
          >
            {basketMessage !== null && <Alert severity="success">{basketMessage}</Alert>}
            {basketItems?.length === 0 && <Alert severity="info">{t("alert:info.basketIsEmpty")}</Alert>}

            {groupedItems.map((data) => (
              <BasketItem data={data} key={data.id} />
            ))}
          </Stack>
          <OrderDrawerFooter>
            <Button
              variant="contained"
              fullWidth
              component={RouterLink}
              onClick={handleStorePath}
              to={routePaths.basket.path}
              disabled={basketItems?.length == 0}
            >
              {t("button:configureBasket")}
              <ArrowForwardRoundedIcon sx={{ ml: 0.5 }} />
            </Button>
          </OrderDrawerFooter>
        </Stack>
      </Stack>
    </OrderDrawer>
  );
};

const OrderDrawer = styled(Drawer, { shouldForwardProp: (prop) => prop !== "open" })(({ theme, open }) => ({
  height: "100vh",
  ".MuiDrawer-paper": {
    position: "relative",
    width: BASKETPANEL_WIDTH,
    height: "100%",
    background: "transparent",
    boxSizing: "border-box",
    border: "none",
    ...(!open && {
      overflowX: "hidden",
      width: 0,
    }),

    transition: theme.transitions.create(["width"], {
      duration: theme.transitions.duration.short,
      easing: theme.transitions.easing.easeOut,
    }),
  },
}));

// const OrderDrawerFooter = styled("div")(({ theme }) => ({
//   padding: theme.spacing(2),
//   textAlign: "center",
// }));

const OrderDrawerFooter = styled("div")(({ theme }) => ({
  position: "sticky",
  bottom: "0",
  left: "0",
  right: "0",
  backgroundColor: theme.palette.background.default,
  borderTop: `1px solid ${theme.palette.backgroundAlpha.border}`,
  padding: theme.spacing(2),
  textAlign: "center",
  marginTop: "auto",
  zIndex: 1,
}));

export default PanelOrder;
